import { useToast } from '@chakra-ui/react';
import axios from 'axios';
import FileSaver from 'file-saver';
import * as _ from 'lodash';
import { useCallback, useState } from 'react';

import { CreditAgreement, Util } from '@finley-technologies/finley-types';

import { useGetReportS3DownloadUrlLazyQueryGQLType } from '../requests/reporting.gql.generated';
import { errorToastConfig } from 'packages/common-frontend/constants';

const _generateFileName = (params: {
  reportName: string;
  reportingDate: Util.FullDateStr;
  version: number;
  isPreview: boolean;
}): string => {
  const { reportName, reportingDate, version, isPreview } = params;
  return `${_.camelCase(reportName)}-${
    isPreview ? 'preview-' : ''
  }${reportingDate}-v${version}`;
};

export const useDownloadReportUrl = (params: {
  id: string;
  reportName: string;
  reportingDate: Util.FullDateStr;
  version: number;
  isPreview: boolean;
  reportType: CreditAgreement.DomainEntity.ReportConfigType;
}) => {
  const { id: reportId, reportType: reportConfigType, reportName } = params;

  const toast = useToast();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [getReportUrlQuery] = useGetReportS3DownloadUrlLazyQueryGQLType({
    fetchPolicy: 'network-only',
    variables: { reportId, reportConfigType },
    onCompleted: async ({ downloadUrl }) => {
      if (!downloadUrl) {
        toast({
          ...errorToastConfig,
          title: `Failed to retrieve valid download URL [report:${reportName}]`,
        });
        return;
      }

      try {
        const response = await axios({
          url: downloadUrl,
          method: 'GET',
          responseType: 'blob',
        });

        const fileBlob = new Blob([response.data], {
          type: response.data.type,
        });
        const fileName = _generateFileName(params);
        FileSaver.saveAs(fileBlob, fileName);
      } catch (_error) {
        toast({
          ...errorToastConfig,
          title: `Failed to download [report:${reportName}]`,
        });
      } finally {
        setIsLoading(false);
      }
    },
    onError: () => {
      // error already logged from gql, so no need to log again
      setIsLoading(false);
    },
  });

  const getReportDownloadUrl = useCallback(async () => {
    setIsLoading(true);
    await getReportUrlQuery();
  }, [getReportUrlQuery]);

  return {
    getReportDownloadUrl,
    isLoading,
  };
};
